<template>
  <div>
    <div class="mb-[26px]">
      <div class="flex h-10">
        <template v-for="(tab, index) in tabs" :key="tab.value">
          <div v-if="index > 0" class="w-2.5 border-b border-[#CCC]/50"></div>
          <button
            type="button"
            class="h-10 flex cursor-pointer items-center border border-[#CCC]/50 rounded-t-xl px-[30px] font-primary text-[14px] leading-[20px] transition-colors"
            :class="tabClasses(tab.value)"
            @click="emit('update:activeSide', tab.value)"
          >
            <span
              class="flex items-center gap-x-2.5 whitespace-nowrap"
              :class="{ 'opacity-40': activeSide !== tab.value }"
            >
              <ThumbnailIcon
                v-if="tab.file"
                :type="tab.file.type"
                :image-url="tab.file.asset?.previewImageUrl"
                :icon-size="16"
                class="size-5 rounded-xs bg-gray-200/25 p-px"
              />

              <Icon v-else name="File" :size="20" class="stroke-gray-700" />

              <span>{{ tab.label }}</span>
            </span>
          </button>
        </template>

        <div class="flex-1 border-b border-[#CCC]/50"></div>
      </div>

      <ReportFileCard
        v-if="activeFile"
        :key="`${activeSide}-${activeFile.id}`"
        :file="activeFile"
        embedded
        class="h-[120px] rounded-tr-xl rounded-b-xl border border-[#CCC]/50 border-t-0"
      />
    </div>

    <div class="border border-[#CCC]/50 rounded-xl">
      <div class="flex items-center gap-x-2.5 px-[30px] py-5">
        <Icon :name="iconName" :size="20" class="stroke-gray-800" />

        <div
          class="font-primary font-medium text-[16px] leading-[18px] text-gray-800"
        >
          {{ count }} potential
          {{ pluralize('infringement', count) }}
        </div>
      </div>

      <div class="w-full h-px bg-[#CCC]/50"></div>

      <div class="px-[30px] py-5">
        <div class="font-secondary text-[13px] leading-[20px] text-gray-500">
          Analyzed with
          <span
            class="w-fit bg-linear-to-r from-yellow-500 via-[#F84600] to-[#CE489A] bg-clip-text font-medium text-transparent"
          >
            Dacr AI
          </span>
        </div>

        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ReportFileCard from '@/features/infringement/components/ReportFileCard.vue'
import { Icon, ThumbnailIcon, type IconName } from '@dacr/ui'
import {
  MatchCategoryType,
  type ReportFile,
} from '@/shared/graphql/router/graphql'
import { computed } from 'vue'
import pluralize from 'pluralize'

export type MatchesSide = 'registered' | 'infringing'

const props = defineProps<{
  count: number
  category?: MatchCategoryType | null
  activeSide: MatchesSide
  registeredFile?: ReportFile | null
  infringingFile?: ReportFile | null
}>()

const emit = defineEmits<{
  'update:activeSide': [side: MatchesSide]
}>()

const tabs = computed(() => [
  {
    value: 'registered' as const,
    label: 'Registration',
    file: props.registeredFile,
  },
  {
    value: 'infringing' as const,
    label: 'Potentially infringing work',
    file: props.infringingFile,
  },
])

const tabClasses = (side: MatchesSide) => ({
  'bg-white text-gray-700 font-medium border-b-white':
    props.activeSide === side,
  'bg-gray-200/20 text-gray-700 font-normal': props.activeSide !== side,
})

const activeFile = computed(() =>
  props.activeSide === 'registered'
    ? props.registeredFile
    : props.infringingFile
)

const iconName = computed<IconName>(() => {
  switch (props.category) {
    case MatchCategoryType.Image:
      return 'Image'
    case MatchCategoryType.Text:
      return 'File'
    case MatchCategoryType.Transcript:
      return 'Text'
    default:
      return 'Menu'
  }
})
</script>
